/**
 * Task 292C1 — the internal «إرسال للكتابة» action.
 *
 * Renders inside the agency-only "الإجراءات المتاحة" card on the content
 * detail surface. It is NOT a TransitionPanel action: the handoff does not
 * change `content_items.status`, so it cannot be expressed as a
 * `ContentItemTransitionTarget` and `ContentItemTransitionTarget` must not be
 * widened to pretend otherwise.
 *
 * Gating is defence-in-depth, not the security boundary. `canSend` mirrors
 * `app.require_writing_handoff_authz` so the button matches the server gate,
 * but every rule is enforced again inside the SECURITY DEFINER RPC — this
 * component cannot grant anything.
 *
 * The writer list comes from `list-eligible-content-writers`, which is gated by
 * the SAME server predicate as the handoff, so the picker can never offer a
 * writer the handoff would reject.
 */

import { useCallback, useEffect, useState } from 'react';

import { listEligibleContentWriters, sendContentItemToWriting } from '../../api/endpoints';
import { isApiError } from '../../api/client';
import type { EligibleContentWriter } from '../../api/types';

export const SEND_TO_WRITING_AR = 'إرسال للكتابة';
export const SEND_TO_WRITING_NO_WRITERS_AR =
  'لا يوجد كاتب محتوى مؤهل مُسنَد إلى هذا العميل. أضف كاتبًا إلى فريق العميل أولًا.';
export const SEND_TO_WRITING_DENIED_AR = 'لا تملك صلاحية إرسال هذا المحتوى للكتابة.';
export const SEND_TO_WRITING_CONFLICT_AR =
  'تغيّرت حالة هذا المحتوى أثناء الإرسال. حدّث الصفحة ثم أعد المحاولة.';
export const SEND_TO_WRITING_SENDING_AR = 'جارٍ الإرسال…';
export const SEND_TO_WRITING_ASSIGNED_BADGE_AR = 'مُسنَد حاليًا';

/** The item states the server accepts. Mirrors the RPC's status gate. */
const PRE_CLIENT_STATUSES: ReadonlyArray<string> = ['draft', 'pending_client_content_approval'];

export function canSendItemToWriting(itemStatus: string, itemDiscipline: string): boolean {
  // Writing is a PRE-client lane and applies to content-discipline items only.
  return PRE_CLIENT_STATUSES.includes(itemStatus) && itemDiscipline === 'content';
}

type Phase =
  | { readonly kind: 'idle' }
  | { readonly kind: 'loading' }
  | { readonly kind: 'ready'; readonly writers: ReadonlyArray<EligibleContentWriter> }
  | { readonly kind: 'error'; readonly message: string };

interface SendToWritingPanelProps {
  readonly contentItemId: string;
  /** Mirrors app.require_writing_handoff_authz. */
  readonly canSend: boolean;
  readonly itemStatus: string;
  readonly itemDiscipline: string;
}

export function SendToWritingPanel(props: SendToWritingPanelProps) {
  const { contentItemId, canSend, itemStatus, itemDiscipline } = props;

  const [phase, setPhase] = useState<Phase>({ kind: 'idle' });
  const [selected, setSelected] = useState<string>('');
  const [note, setNote] = useState<string>('');
  const [inFlight, setInFlight] = useState(false);
  const [submitError, setSubmitError] = useState<string | null>(null);
  const [success, setSuccess] = useState<string | null>(null);

  const eligible = canSend && canSendItemToWriting(itemStatus, itemDiscipline);

  const load = useCallback(
    async (signal?: AbortSignal) => {
      setPhase({ kind: 'loading' });
      try {
        const data = await listEligibleContentWriters({ contentItemId, signal });
        setPhase({ kind: 'ready', writers: data.writers });
        // Preselect whoever already holds the writing task, so a re-send
        // defaults to the no-op rather than to a silent reassignment.
        const current = data.writers.find((w) => w.is_currently_assigned);
        if (current) setSelected(current.user_id);
      } catch (err) {
        if (signal?.aborted) return;
        setPhase({ kind: 'error', message: describeError(err) });
      }
    },
    [contentItemId],
  );

  useEffect(() => {
    if (!eligible) return;
    const ac = new AbortController();
    void load(ac.signal);
    return () => ac.abort();
  }, [eligible, load]);

  // Selection must not survive a move to a different content item.
  useEffect(() => {
    setSelected('');
    setNote('');
    setSuccess(null);
    setSubmitError(null);
  }, [contentItemId]);

  const onSend = useCallback(async () => {
    if (inFlight || !selected) return;
    // Duplicate-click prevention. The server is idempotent regardless (the RPC
    // locks the content_item row), so this is a UX guard, not the guarantee.
    setInFlight(true);
    setSubmitError(null);
    setSuccess(null);
    try {
      const res = await sendContentItemToWriting({
        input: {
          content_item_id: contentItemId,
          assignee_user_id: selected,
          note: note.trim().length > 0 ? note.trim() : null,
        },
      });
      setSuccess(
        res.assignment_already_existed
          ? 'هذا المحتوى مُسنَد بالفعل إلى هذا الكاتب.'
          : res.task_already_existed
            ? 'تم إعادة فتح مهمة الكتابة وإسنادها.'
            : 'تم إرسال المحتوى للكتابة.',
      );
      setNote('');
      // Refresh so the inline writing-task state (who holds it now) is
      // authoritative rather than optimistic.
      await load();
    } catch (err) {
      setSubmitError(describeError(err));
    } finally {
      setInFlight(false);
    }
  }, [contentItemId, inFlight, load, note, selected]);

  if (!eligible) return null;

  return (
    <div className="send-to-writing" style={{ marginTop: 18 }}>
      <h3 style={{ fontSize: 15, margin: '0 0 10px' }}>{SEND_TO_WRITING_AR}</h3>

      {phase.kind === 'loading' ? <p className="muted">جارٍ تحميل الكتّاب…</p> : null}

      {phase.kind === 'error' ? (
        <div className="banner banner-error" role="alert">
          {phase.message}
        </div>
      ) : null}

      {phase.kind === 'ready' && phase.writers.length === 0 ? (
        <div className="banner banner-info" role="note">
          {SEND_TO_WRITING_NO_WRITERS_AR}
        </div>
      ) : null}

      {phase.kind === 'ready' && phase.writers.length > 0 ? (
        <>
          <label style={{ display: 'block', marginBottom: 8 }}>
            <span style={{ display: 'block', marginBottom: 4 }}>الكاتب</span>
            <select
              value={selected}
              disabled={inFlight}
              onChange={(e) => setSelected(e.target.value)}
            >
              <option value="">— اختر كاتبًا —</option>
              {phase.writers.map((w) => (
                <option key={w.user_id} value={w.user_id}>
                  {w.full_name || w.user_id}
                  {w.is_currently_assigned ? ` (${SEND_TO_WRITING_ASSIGNED_BADGE_AR})` : ''}
                </option>
              ))}
            </select>
          </label>

          <label style={{ display: 'block', marginBottom: 10 }}>
            <span style={{ display: 'block', marginBottom: 4 }}>ملاحظة للكاتب (اختياري)</span>
            <textarea
              value={note}
              rows={2}
              maxLength={2000}
              disabled={inFlight}
              onChange={(e) => setNote(e.target.value)}
            />
          </label>

          <button type="button" className="btn" disabled={inFlight || !selected} onClick={onSend}>
            {inFlight ? SEND_TO_WRITING_SENDING_AR : SEND_TO_WRITING_AR}
          </button>

          {submitError ? (
            <div className="banner banner-error" role="alert" style={{ marginTop: 10 }}>
              {submitError}
            </div>
          ) : null}
          {success ? (
            <div className="banner banner-success" role="status" style={{ marginTop: 10 }}>
              {success}
            </div>
          ) : null}
        </>
      ) : null}
    </div>
  );
}

/** Map the shared API error envelope onto the Arabic states this task requires. */
export function describeError(err: unknown): string {
  // Never surface err.message for a typed API error (ContentPage.tsx:1001) —
  // map the status onto the Arabic states this task specifies.
  if (isApiError(err)) {
    if (err.status === 403) return SEND_TO_WRITING_DENIED_AR;
    if (err.status === 409) return SEND_TO_WRITING_CONFLICT_AR;
    if (err.status === 404) return 'لم يعد هذا المحتوى متاحًا.';
    if (err.status === 400) return 'لا يمكن إرسال هذا المحتوى للكتابة في حالته الحالية.';
  }
  return 'تعذّر إتمام العملية. حاول مرة أخرى.';
}
